import { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { GET_USERS } from "../../constant";
import { DataGrid } from "../index";
import { start, done } from "../../store/loaderSlice";
import { convertTimestamp } from "../../utils/convertTimestamp";
import { snackbarStart } from "../../store/SnackbarSlice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CSVLink } from "react-csv";
import CreateUser from "./CreateUser";
import CreateUserFromExcel from "./CreateUserFromExcel";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

const DashboardUsers = () => {
  const { token } = useSelector((state) => state.user);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editUserData, setEditUserData] = useState(null);

  const fetchUsers = async () => {
    dispatch(start());
    try {
      const { data } = await axios.get(GET_USERS, {
        headers: { "x-auth-token": token },
      });
      setUsers(data);
    } catch (err) {
      console.error(err);
      navigate("/");
    } finally {
      dispatch(done());
    }
  };

  const handleDeleteUsers = async () => {
    if (!selectedIds.length) return;
    dispatch(start());
    try {
      await axios.delete(GET_USERS, {
        headers: { "Content-Type": "application/json", "x-auth-token": token },
        data: { ids: selectedIds },
      });
      dispatch(
        snackbarStart({
          text: t("usersDashboard.deletedMessage"),
          severity: "success",
        }),
      );
      fetchUsers();
    } catch {
      dispatch(
        snackbarStart({ text: t("somethingWentWrong"), severity: "error" }),
      );
    } finally {
      dispatch(done());
      setSelectedIds([]);
    }
  };

  const handleAddUser = () => {
    setEditUserData(null);
    setShowForm(true);
    setShowImport(false);
  };

  const handleEditUser = () => {
    if (selectedIds.length !== 1) {
      dispatch(
        snackbarStart({
          text: t("selectOneItem"),
          severity: "warning",
        }),
      );
      return;
    }
    const target = users.find((u) => u._id === selectedIds[0]);
    setEditUserData(target);
    setShowForm(true);
    setShowImport(false);
    setSelectedIds([]);
  };

  const handleImportExcel = () => {
    setShowImport(true);
    setShowForm(false);
  };

  const onSelectionChange = (ids) => setSelectedIds(ids);

  const columns = [
    { field: "id", headerName: "ID", width: 220 },
    { field: "name", headerName: t("usersDashboard.name"), width: 180 },
    { field: "email", headerName: t("usersDashboard.email"), width: 220 },
    { field: "role", headerName: t("usersDashboard.role"), width: 100 },
    {
      field: "registeredAt",
      headerName: t("usersDashboard.registeredTime"),
      width: 150,
    },
    {
      field: "lastLoginAt",
      headerName: t("usersDashboard.lastLoginTime"),
      width: 150,
    },
    { field: "status", headerName: t("usersDashboard.status"), width: 110 },
    { field: "testCount", headerName: t("usersDashboard.tests"), width: 90 },
  ];

  const rows = users.map((u) => ({
    id: u._id,
    name: u.name,
    email: u.email,
    role: u.isAdmin ? t("admin") : t("notAdmin"),
    registeredAt: convertTimestamp(u.registerTime),
    lastLoginAt: convertTimestamp(u.lastLoginTime),
    status: u.status ? t("active") : t("blocked"),
    testCount: u.availableTests?.length || 0,
  }));

  const csvHeaders = columns.map(({ headerName, field }) => ({
    label: headerName,
    key: field,
  }));

  useEffect(() => {
    if (token) fetchUsers();
  }, [token]);

  if (!token) return null;

  return (
    <section className="min-h-screen pt-24 pb-16 bg-gray-50 dark:bg-[#030712] transition-colors duration-300">
      <div className="container mx-auto px-4">
        {/* Page header */}
        <div className="flex items-center gap-4 mb-8 mt-2">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500 flex-shrink-0">
            <PeopleAltIcon sx={{ fontSize: 26 }} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">
              {t("dashboard.users")}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t("usersDashboard.total")}: {users.length}
            </p>
          </div>
        </div>

        {/* Action toolbar */}
        <div className="flex flex-wrap gap-2.5 mb-5">
          <button
            onClick={handleAddUser}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-semibold transition-all shadow-sm hover:shadow-green-500/30 hover:shadow-md"
          >
            <AddIcon sx={{ fontSize: 17 }} /> {t("usersDashboard.addUser")}
          </button>

          <button
            onClick={handleEditUser}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <EditIcon sx={{ fontSize: 17 }} /> {t("edit") || "Edit"}
          </button>

          <button
            onClick={handleDeleteUsers}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <DeleteIcon sx={{ fontSize: 17 }} /> {t("usersDashboard.delete")}
          </button>

          <CSVLink
            data={rows}
            headers={csvHeaders}
            filename="users.csv"
            className="no-underline"
          >
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-white text-sm font-semibold transition-all">
              <FileDownloadIcon sx={{ fontSize: 17 }} /> {t("exportToCsv")}
            </button>
          </CSVLink>

          <button
            onClick={handleImportExcel}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <UploadFileIcon sx={{ fontSize: 17 }} />{" "}
            {t("importFromExcel") || "Import Excel"}
          </button>
        </div>

        {/* Data grid */}
        <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-[#0f172a] shadow-sm">
          <DataGrid
            rows={rows}
            columns={columns}
            title=""
            checkboxSelection
            selectedIds={selectedIds}
            sx={{
              border: "none",
              "& .MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
            }}
            onRowsSelectionHandler={onSelectionChange}
          />
        </div>

        {/* Modals */}
        {showForm && (
          <div className="mt-8">
            <CreateUser
              setFormStatus={setShowForm}
              getData={fetchUsers}
              editData={editUserData}
            />
          </div>
        )}
        {showImport && (
          <div className="mt-8">
            <CreateUserFromExcel
              setImportFromExcel={setShowImport}
              getData={fetchUsers}
              onClose={() => setShowImport(false)}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default DashboardUsers;
